import React from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { FlipperProps } from "flip-toolkit/lib/types";
import { cva, VariantProps } from "class-variance-authority";

const logoPrimaryStyles = cva("", {
  variants: {
    variant: {
      sidebar: "fill-white",
      login: "fill-midnight dark:fill-white",
    },
  },
});

const logoSecondaryStyles = cva("", {
  variants: {
    variant: {
      sidebar: "fill-azure",
      login: "fill-azure",
    },
  },
});

export const EdgifyLogo = React.forwardRef<
  SVGSVGElement,
  React.SVGProps<SVGSVGElement> & {
    full?: boolean;
    primary?: React.SVGProps<SVGPathElement>;
    secondary?: React.SVGProps<SVGPathElement>;
    parent?: Omit<FlipperProps, "children" | "flipKey">;
  } & VariantProps<typeof logoPrimaryStyles> &
    VariantProps<typeof logoSecondaryStyles>
>(
  (
    {
      full,
      variant,
      primary: _primary,
      secondary: _secondary,
      parent = {},
      ...props
    },
    ref,
  ) => {
    const primary = {
      ..._primary,
      className: logoPrimaryStyles({ className: _primary?.className, variant }),
    };
    const secondary = {
      ..._secondary,
      className: logoSecondaryStyles({
        className: _secondary?.className,
        variant,
      }),
    };
    return (
      <Flipper {...parent} flipKey={full}>
        {full ? (
          <svg
            ref={ref}
            width="73"
            height="30"
            viewBox="0 -2 73 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...props}
          >
            <Flipped flipId={"star"}>
              <path
                d="M45.6828 1.58978C46.1389 2.82202 47.1094 3.79248 48.3416 4.24863C47.1094 4.70477 46.1389 5.67523 45.6828 6.90747C45.2266 5.67523 44.2562 4.70477 43.0239 4.24863C44.2562 3.79248 45.2266 2.82202 45.6828 1.58978Z"
                {...secondary}
              />
            </Flipped>
            <path
              d="M44.2456 21.2901V8.28272H47.0856V21.2901H44.2456Z"
              {...primary}
            />
            <path
              d="M56.9239 1.46274C57.1606 1.46274 57.3629 1.4667 57.5321 1.47596C57.7014 1.48521 57.8706 1.50505 58.0398 1.53942V3.99863C57.9209 3.98144 57.8032 3.96954 57.6855 3.96028C57.5665 3.95235 57.4145 3.94706 57.2294 3.94706C56.5022 3.94706 55.9151 4.16654 55.4669 4.60682C55.0187 5.04709 54.7953 5.64603 54.7953 6.40759V8.28372H57.9896V10.7429H54.7953V21.2911H51.9553V10.7429H49.4961V8.28372H51.9553V6.48295C51.9553 4.91091 52.4035 3.68131 53.2986 2.79415C54.1937 1.90698 55.4035 1.46274 56.9239 1.46274Z"
              {...primary}
            />
            <path
              d="M61.6149 27.7567L64.8092 20.3778L59.6621 8.28272H62.8313L66.331 17.1835L69.8308 8.28272H73L64.7339 27.7567H61.6149Z"
              {...primary}
            />
            <path
              d="M23.9111 1.58978V15.1299C23.9111 16.4323 23.5647 17.4159 22.8719 18.0836C22.1791 18.7513 21.2998 19.0858 20.2342 19.0858C19.6762 19.0858 19.1646 18.9933 18.7005 18.8068C18.2351 18.6217 17.8345 18.3467 17.496 17.9831C17.1575 17.6196 16.8918 17.1713 16.6974 16.6398C16.5031 16.107 16.4052 15.4949 16.4052 14.802C16.4052 14.1092 16.5017 13.4958 16.6974 12.9643C16.8918 12.4314 17.1575 11.9793 17.496 11.6077C17.8345 11.2362 18.2351 10.9572 18.7005 10.7708C19.1646 10.5857 19.6762 10.4918 20.2342 10.4918C21.1134 10.4918 21.8657 10.7232 22.4911 11.1833V8.66858C22.4329 8.63817 22.3747 8.60644 22.3139 8.57603C21.6039 8.22963 20.7591 8.05643 19.778 8.05643C18.8657 8.05643 18.0288 8.21244 17.2673 8.52579C16.507 8.83914 15.8473 9.28603 15.2893 9.8691C14.7314 10.4522 14.2964 11.1622 13.983 11.9991C13.6697 12.836 13.5137 13.7694 13.5137 14.8007C13.5137 15.832 13.6697 16.7654 13.983 17.6024C14.2951 18.4393 14.7314 19.1493 15.2893 19.7323C15.8473 20.3154 16.507 20.7636 17.2673 21.0757C18.0275 21.3877 18.8644 21.545 19.778 21.545C20.7921 21.545 21.6714 21.3507 22.4144 20.962C23.1575 20.5732 23.7075 20.0827 24.0631 19.4917V21.2925H26.7511V1.58978H23.9111Z"
              {...primary}
            />
            <path
              d="M3.01715 4.98742H11.9179V2.3246H0V21.2909H11.9179V18.6029H3.01715V16.2826V12.8225H10.802V10.2099H3.01715V5.6789V4.98742Z"
              {...primary}
            />
            <path
              d="M39.0986 8.28347V10.1094C38.743 9.51836 38.1943 9.02784 37.4499 8.63913C36.7055 8.25042 35.8276 8.05606 34.8122 8.05606C33.8999 8.05606 33.063 8.21207 32.3014 8.52542C31.5412 8.83877 30.8815 9.28566 30.3235 9.86873C29.7656 10.4518 29.3306 11.1618 29.0172 11.9987C28.7039 12.8356 28.5479 13.7691 28.5479 14.8004C28.5479 15.8316 28.7039 16.7862 29.0172 17.6152C29.3292 18.4442 29.7656 19.1489 30.3235 19.732C30.8815 20.3151 31.5412 20.7633 32.3014 21.0753C33.0617 21.3873 33.8986 21.5447 34.8122 21.5447C35.7919 21.5447 36.6381 21.3715 37.3481 21.0251C37.4089 20.9946 37.4671 20.9642 37.5253 20.9325V18.4178C36.8999 18.8779 36.1476 19.1093 35.2684 19.1093C34.7104 19.1093 34.1987 19.0167 33.7347 18.8303C33.2693 18.6452 32.8686 18.3662 32.5302 17.9934C32.1917 17.6218 31.926 17.1697 31.7316 16.6368C31.5372 16.104 31.4394 15.4918 31.4394 14.799C31.4394 14.1062 31.5359 13.4928 31.7316 12.9612C31.926 12.4284 32.1917 11.9815 32.5302 11.6179C32.8686 11.2543 33.2693 10.9807 33.7347 10.7942C34.1987 10.6091 34.7104 10.5153 35.2684 10.5153C36.3327 10.5153 37.2119 10.8498 37.906 11.5175C38.5989 12.1851 38.9453 13.1701 38.9453 14.4711V22.129C38.9453 23.2608 38.6108 24.1189 37.9431 24.7033C37.2754 25.2863 36.4001 25.5785 35.3186 25.5785C34.3547 25.5785 33.5562 25.3591 32.9229 24.9188C32.2895 24.4785 31.9048 23.8796 31.7686 23.118L28.9286 23.2198C29.0463 23.9298 29.275 24.5764 29.6135 25.1594C29.952 25.7425 30.387 26.2409 30.9198 26.6561C31.4526 27.0699 32.0899 27.3952 32.8343 27.6318C33.5773 27.8685 34.4314 27.9862 35.3953 27.9862C36.3591 27.9862 37.2331 27.8474 38.0198 27.5684C38.8051 27.2894 39.4781 26.8875 40.036 26.3639C40.594 25.8403 41.025 25.2017 41.3291 24.4494C41.6332 23.6971 41.7852 22.8562 41.7852 21.9268V8.28347H39.0973H39.0986Z"
              {...primary}
            />
          </svg>
        ) : (
          <svg
            ref={ref}
            width="21"
            height="30"
            viewBox="0 -2 21 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...props}
          >
            <Flipped flipId={"star"}>
              <path
                d="M16.6588 15C17.115 16.2322 18.0855 17.2027 19.3177 17.6588C18.0855 18.115 17.115 19.0855 16.6588 20.3177C16.2027 19.0855 15.2322 18.115 14 17.6588C15.2322 17.2027 16.2027 16.2322 16.6588 15Z"
                {...secondary}
              />
            </Flipped>
            <path
              d="M3.01715 4.28464H11.9179V1.62183H0V20.5881H11.9179V17.9002H3.01715V15.5798V12.1197H10.802V9.50714H3.01715V4.97613V4.28464Z"
              {...primary}
            />
          </svg>
        )}
        {props.children}
      </Flipper>
    );
  },
);
